import './flow-action-bar.css';

import { IonIcon, IonItem, IonLabel, IonList } from '@ionic/react';
import {
  checkmark,
  checkmarkCircleOutline,
  checkmarkDone,
  colorWandOutline,
} from 'ionicons/icons';
import { ReactElement, useCallback } from 'react';
import { useGetScratchpadQuery } from '../../../../../api/graphql/generated';
import { ActionBarIcon } from '../../../../../components/button/action-bar-icon';
import { ActionButton } from '../../../../../components/button/action-button';
import { ScratchpadAttachmentActionButton } from '../../../../../components/machine/scratchpad/scratchpad-attachment-action-button';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../../pages/machine/machine-context-provider';
import { useHasStepMode } from '../../hook/use-has-step-mode';
import { Flow, FlowState, FlowValidateError } from '../../model/flow.types';
import { FlowRef } from '../flow/flow-ref';

interface FlowActionBarProps {
  flow: Flow<unknown> & {
    config: { stepModeEnabled?: boolean };
  };
  readOnly?: boolean;
  flowValidationErrors?: FlowValidateError[] | undefined;
  flowRef: FlowRef;
  scratchpadId: string | undefined;
}

const FlowActionBar = (props: FlowActionBarProps): ReactElement => {
  const { readOnly, flow, flowRef, scratchpadId } = props;

  const {
    updateFlowConfig,
    setFlowState,
    flowState,
    componentRegistry: { FlowActionBarEditActions },
  } = flowRef;

  const { hideAttachments } = useMachineContext();
  const hasStepMode = useHasStepMode();

  const toggleTeachState = () => {
    const newState =
      flowState === FlowState.DEFAULT ? FlowState.EDIT : FlowState.DEFAULT;

    setFlowState(newState);
  };

  const modeActionDisabled = flowRef.flowState === FlowState.TEACH;
  const multiSelectDisabled = flowRef.flowState === FlowState.TEACH;

  const teachDisabled =
    readOnly ||
    flowRef.flowState === FlowState.TEACH ||
    flowRef.selectedItemDetails?.selectedItemIds?.length > 0;

  const toggleStepMode = useCallback(
    (enabled: boolean) => {
      if (typeof updateFlowConfig === 'function') {
        updateFlowConfig({
          stepModeEnabled: enabled,
        });
      }
    },
    [updateFlowConfig]
  );

  const isStepModeEnabled = flow.config.stepModeEnabled;

  const runModeLabel = <>{getLocalizedText('common:run_mode')}</>;
  const stepModeLabel = getLocalizedText('common:step_mode');

  const { data: scratchpadData } = useGetScratchpadQuery({
    variables: {
      input: {
        id: String(scratchpadId),
      },
    },
    skip: scratchpadId === undefined,
  });

  return (
    <>
      {!readOnly && hasStepMode && (
        <ActionButton
          buttonProps={{
            fill: 'clear',
            slot: 'start',
            color: 'text',
            disabled: modeActionDisabled,
            style: {
              marginLeft: '14px',
            },
          }}
          className="ion-no-padding"
          label={isStepModeEnabled ? stepModeLabel : runModeLabel}
        >
          {({ close }) => (
            <IonList>
              <IonItem
                onClick={() => {
                  toggleStepMode(false);
                  setFlowState(FlowState.DEFAULT);
                  close();
                }}
                detailIcon={checkmarkCircleOutline}
              >
                <IonLabel>{runModeLabel}</IonLabel>
                {flowState === FlowState.DEFAULT && !isStepModeEnabled && (
                  <IonIcon slot="end" icon={checkmark} />
                )}
              </IonItem>
              <IonItem
                lines="none"
                onClick={() => {
                  toggleStepMode(true);
                  close();
                }}
              >
                <IonLabel>{stepModeLabel}</IonLabel>
                {isStepModeEnabled && <IonIcon slot="end" icon={checkmark} />}
              </IonItem>
            </IonList>
          )}
        </ActionButton>
      )}
      {!hideAttachments && (
        <ScratchpadAttachmentActionButton
          scratchpad={scratchpadData?.getScratchpad}
        />
      )}
      <ActionBarIcon
        isHighlighted={false}
        disabled={multiSelectDisabled}
        popoverClassName="edit-actions-popover"
        popoverContents={
          <FlowActionBarEditActions flow={flow} flowRef={flowRef} />
        }
      >
        <IonIcon icon={checkmarkDone} slot="icon-only" />
      </ActionBarIcon>
      <ActionBarIcon
        isHighlighted={flowState === FlowState.EDIT}
        onToggle={toggleTeachState}
        disabled={teachDisabled}
      >
        <IonIcon icon={colorWandOutline} slot="icon-only" />
      </ActionBarIcon>
    </>
  );
};

export { FlowActionBar };
